// Steering wheel setup: connect (WebHID), live view of every control, guided
// calibration and per-control "learn" bindings, force feedback tests and the
// wheel settings. Opened from the start screen and the pause menu.

import { setParam, type Param } from '../config/params';
import { ACTION_LABELS, GEAR_ACTIONS, Learner, defaultProfile, pedalValue, steerValue, type Action, type PedalRole } from '../wheel/bindings';
import { AID_PRESETS, GEARBOX_MODES, aidPreset, applyAidPreset, gearboxMode, wheelParams } from '../wheel/settings';
import type { WheelManager } from '../wheel/wheelManager';

const TC_LABELS = ['Off', 'Low', 'High'];

type Target = 'centre' | 'steer' | PedalRole | Action | 'direction';

const PEDAL_LABELS: Record<PedalRole, string> = { throttle: 'Throttle pedal', brake: 'Brake pedal', clutch: 'Clutch pedal' };
const BUTTON_ACTIONS: Action[] = ['upshift', 'downshift', 'select', 'pause', 'box', 'reset', 'camera', ...GEAR_ACTIONS];

/**
 * Guided setup: everything a wheel player needs from the rim and paddles. The
 * clutch and the H-shifter gates are only asked for in H-shifter mode.
 */
function guidedSteps(): Target[] {
  const h = gearboxMode() === 'hpattern';
  return [
    'centre',
    'steer',
    'throttle',
    'brake',
    ...(h ? (['clutch'] as Target[]) : []),
    'upshift',
    'downshift',
    'select',
    'pause',
    'box',
    'reset',
    ...(h ? (['gear1', 'gear2', 'gear3', 'gear4', 'gear5', 'gear6', 'reverse'] as Target[]) : []),
    'direction',
  ];
}

function prompt(t: Target): string {
  switch (t) {
    case 'centre':
      return 'Centre the wheel and take your feet off the pedals, then press <b>Next</b> (or the wheel\'s OK button).';
    case 'steer':
      return 'Turn the wheel to <b>full right lock</b>, then back to the centre.';
    case 'throttle':
    case 'brake':
    case 'clutch':
      return `Press the <b>${PEDAL_LABELS[t].toLowerCase()}</b> all the way down, then let it go.`;
    case 'direction':
      return '<b>Let go of the wheel</b>, then press <b>Next</b> or OK: it will turn a little by itself so the game can learn which way your wheel pushes.';
    default:
      if ((GEAR_ACTIONS as readonly string[]).includes(t)) return `Put the shifter into <b>${ACTION_LABELS[t as Action].replace('Shifter ', '')}</b> (Skip if you don't use the H-shifter).`;
      if (t === 'upshift' || t === 'downshift') return `Pull the <b>${t === 'upshift' ? 'right' : 'left'} paddle</b> (${t === 'upshift' ? 'upshift, and next item in menus' : 'downshift, and previous item in menus'}).`;
      if (t === 'select') return 'Press the wheel button you want for <b>OK</b> in menus (the top right button on a G27, ✕ on a G29).';
      if (t === 'pause') return 'Press the wheel button you want for <b>Pause</b> (also Back in menus; Options on a G29).';
      return `Press the wheel button you want for <b>${ACTION_LABELS[t as Action]}</b> (Skip if you don't want one).`;
  }
}

export class WheelSetup {
  private readonly el: HTMLElement;
  open = false;
  onClose: (() => void) | undefined;
  private raf = 0;
  private learning: { target: Target; learner?: Learner; guided: boolean; started: number } | undefined;
  private guidedIndex = -1;
  private steps: Target[] = guidedSteps();
  private readonly q = <T extends HTMLElement>(sel: string): T => this.el.querySelector(sel) as T;

  constructor(
    root: HTMLElement,
    private readonly wheel: WheelManager,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'overlay settings wheel-setup';
    this.el.innerHTML = `
      <div class="menu-title">Steering wheel</div>
      <p class="wheel-status"></p>
      <div class="wheel-actions">
        <button class="settings-btn cycle" data-act="connect">Connect wheel</button>
        <button class="settings-btn cycle" data-act="guided">Guided setup</button>
        <button class="settings-btn cycle" data-act="direction">Check force direction</button>
        <button class="settings-btn cycle" data-act="left">Push left</button>
        <button class="settings-btn cycle" data-act="right">Push right</button>
        <button class="settings-btn cycle" data-act="native">Switch to native mode</button>
        <button class="settings-btn cycle" data-act="native-g29">Switch to native mode (G29)</button>
        <button class="settings-btn cycle" data-act="disconnect">Disconnect</button>
      </div>
      <div class="wheel-live">
        <div class="wheel-steer"><div class="wheel-steer-fill"></div><div class="wheel-steer-mid"></div><span class="wheel-steer-label"></span></div>
        <div class="wheel-pedals">
          <div class="wheel-pedal" data-p="clutch"><div></div><span>C</span></div>
          <div class="wheel-pedal" data-p="brake"><div></div><span>B</span></div>
          <div class="wheel-pedal" data-p="throttle"><div></div><span>T</span></div>
        </div>
        <div class="wheel-readout"></div>
        <div class="wheel-ffb"><div class="wheel-ffb-fill"></div><div class="wheel-steer-mid"></div><span>Force</span></div>
      </div>
      <div class="wheel-prompt"><span class="wheel-prompt-text"></span><div class="wheel-prompt-progress"><div></div></div>
        <button class="settings-btn cycle" data-act="next">Next</button>
        <button class="settings-btn cycle" data-act="skip">Skip</button>
        <button class="settings-btn cycle" data-act="cancel">Stop</button>
      </div>
      <div class="settings-grid wheel-settings"></div>
      <div class="menu-subtitle">Controls</div>
      <div class="settings-grid wheel-bindings"></div>
      <div class="settings-footer">
        <button class="settings-btn secondary" data-act="reset">Reset controls</button>
        <button class="settings-btn primary" data-act="done">Done</button>
      </div>`;
    root.appendChild(this.el);
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.el.addEventListener('click', (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act) void this.action(act);
      const learn = (e.target as HTMLElement).closest<HTMLElement>('[data-learn]')?.dataset.learn;
      if (learn) this.startLearn(learn as Target, false);
      const clear = (e.target as HTMLElement).closest<HTMLElement>('[data-clear]')?.dataset.clear;
      if (clear) this.clearBinding(clear as Target);
    });
    this.buildSettings();
    this.wheel.onChange = () => {
      if (!this.open) return;
      if (this.wheel.hid) this.wheel.hid.onInput = () => this.learnTick();
      this.render();
    };
  }

  private buildSettings(): void {
    const grid = this.q('.wheel-settings');
    const pct = (p: Param): string => `${Math.round(p.value * 100)}%`;
    const rows: Array<{ label: string; hint?: string; value: () => string; dec?: () => void; inc?: () => void; cycle?: () => void }> = [
      {
        label: 'Wheel rotation',
        hint: 'Lock to lock; 360° is F1-like',
        value: () => `${wheelParams.rotation.value}°`,
        dec: () => this.nudge(wheelParams.rotation, -30, () => this.wheel.applyRange()),
        inc: () => this.nudge(wheelParams.rotation, 30, () => this.wheel.applyRange()),
      },
      {
        label: 'Gearbox',
        hint: 'Paddles are how F1 cars shift',
        value: () => GEARBOX_MODES[Math.round(wheelParams.gearbox.value)] ?? 'Paddles',
        cycle: () => setParam(wheelParams.gearbox.id, (Math.round(wheelParams.gearbox.value) + 1) % 3),
      },
      {
        label: 'Driving aids',
        hint: 'Real F1 has none of them',
        value: () => AID_PRESETS[aidPreset()]?.name ?? 'Custom',
        cycle: () => applyAidPreset(aidPreset() + 1),
      },
      {
        label: 'Traction control',
        hint: 'Low lets the rear slide a little, then cuts power',
        value: () => TC_LABELS[Math.round(wheelParams.tc.value)] ?? 'Off',
        cycle: () => setParam(wheelParams.tc.id, (Math.round(wheelParams.tc.value) + 1) % 3),
      },
      {
        label: 'Anti-lock brakes',
        value: () => (wheelParams.abs.value ? 'On' : 'Off'),
        cycle: () => setParam(wheelParams.abs.id, wheelParams.abs.value ? 0 : 1),
      },
      {
        label: 'Stability aid',
        hint: 'Gently calms a sliding rear',
        value: () => (wheelParams.stability.value ? 'On' : 'Off'),
        cycle: () => setParam(wheelParams.stability.id, wheelParams.stability.value ? 0 : 1),
      },
      { label: 'Force strength', hint: 'Overall', value: () => pct(wheelParams.gain), dec: () => this.nudge(wheelParams.gain, -0.05), inc: () => this.nudge(wheelParams.gain, 0.05) },
      { label: 'Tyre forces', hint: 'Steering weight, going light', value: () => pct(wheelParams.aligning), dec: () => this.nudge(wheelParams.aligning, -0.1), inc: () => this.nudge(wheelParams.aligning, 0.1) },
      { label: 'Kerbs and bumps', hint: 'Rumble, impacts, lock-ups', value: () => pct(wheelParams.effects), dec: () => this.nudge(wheelParams.effects, -0.1), inc: () => this.nudge(wheelParams.effects, 0.1) },
      { label: 'Damper', hint: 'Calms the wheel; less is livelier', value: () => pct(wheelParams.damper), dec: () => this.nudge(wheelParams.damper, -0.05), inc: () => this.nudge(wheelParams.damper, 0.05) },
      { label: 'Friction', hint: 'Steady weight at all speeds', value: () => pct(wheelParams.friction), dec: () => this.nudge(wheelParams.friction, -0.02), inc: () => this.nudge(wheelParams.friction, 0.02) },
      { label: 'Minimum force', hint: "Overcomes the wheel's gear deadband", value: () => pct(wheelParams.minForce), dec: () => this.nudge(wheelParams.minForce, -0.01), inc: () => this.nudge(wheelParams.minForce, 0.01) },
      { label: 'Brake curve', hint: 'Higher = finer at light pressure', value: () => wheelParams.brakeGamma.value.toFixed(1), dec: () => this.nudge(wheelParams.brakeGamma, -0.1), inc: () => this.nudge(wheelParams.brakeGamma, 0.1) },
      { label: 'Pedal dead zone', value: () => pct(wheelParams.pedalDeadZone), dec: () => this.nudge(wheelParams.pedalDeadZone, -0.01), inc: () => this.nudge(wheelParams.pedalDeadZone, 0.01) },
      { label: 'Rev LEDs', value: () => (wheelParams.leds.value ? 'On' : 'Off'), cycle: () => setParam(wheelParams.leds.id, wheelParams.leds.value ? 0 : 1) },
    ];
    for (const row of rows) {
      const r = document.createElement('div');
      r.className = 'setting';
      r.innerHTML = `<div class="setting-label">${row.label}${row.hint ? `<small>${row.hint}</small>` : ''}</div><div class="setting-control"></div>`;
      const control = r.querySelector('.setting-control') as HTMLElement;
      const value = document.createElement('span');
      value.className = 'setting-value';
      const btn = (text: string, kind: string, run: () => void): HTMLButtonElement => {
        const b = document.createElement('button');
        b.className = `settings-btn ${kind}`;
        b.textContent = text;
        b.addEventListener('click', () => {
          run();
          this.render();
        });
        return b;
      };
      if (row.dec && row.inc) control.append(btn('−', 'step', row.dec), value, btn('+', 'step', row.inc));
      else if (row.cycle) {
        const b = btn('', 'cycle', row.cycle);
        b.appendChild(value);
        control.append(b);
      }
      this.valueRenderers.push(() => (value.textContent = row.value()));
      grid.appendChild(r);
    }
  }

  private readonly valueRenderers: Array<() => void> = [];

  private nudge(p: Param, delta: number, after?: () => void): void {
    setParam(p.id, Math.round(Math.min(p.max, Math.max(p.min, p.value + delta)) * 1000) / 1000);
    after?.();
  }

  /**
   * Learning a control (or just finished): wheel presses belong to the learner,
   * not menu navigation, so the press that completes a step doesn't also click.
   */
  get capturing(): boolean {
    return !!this.learning?.learner || performance.now() - this.learnedAt < 400;
  }

  /** The wheel's OK button on a step that waits for the player (centre, force direction): same as Next. */
  confirm(): boolean {
    if (!this.learning || this.learning.learner || performance.now() - this.learnedAt < 400) return false;
    void this.next().then(() => this.render());
    return true;
  }

  private learnedAt = 0;

  show(): void {
    this.open = true;
    this.el.classList.add('show');
    this.wheel.applyRange();
    this.render();
    // Learn from every HID report (not just once per frame), so a quick pedal stab still registers.
    if (this.wheel.hid) this.wheel.hid.onInput = () => this.learnTick();
    const loop = (): void => {
      if (!this.open) return;
      this.live();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  close(): void {
    this.open = false;
    this.learning = undefined;
    this.guidedIndex = -1;
    if (this.wheel.hid) this.wheel.hid.onInput = undefined;
    cancelAnimationFrame(this.raf);
    this.el.classList.remove('show');
    this.onClose?.();
  }

  private async action(act: string): Promise<void> {
    const w = this.wheel;
    switch (act) {
      case 'connect':
        await w.request();
        break;
      case 'disconnect':
        await w.disconnect();
        break;
      case 'native':
        w.hid?.switchToNative();
        break;
      case 'native-g29':
        w.hid?.switchToNative('G29');
        break;
      case 'left':
        w.testForce(-0.4, 400);
        break;
      case 'right':
        w.testForce(0.4, 400);
        break;
      case 'direction':
        this.startLearn('direction', false);
        break;
      case 'guided':
        this.steps = guidedSteps();
        this.guidedIndex = 0;
        this.startLearn(this.steps[0]!, true);
        break;
      case 'next':
        await this.next();
        break;
      case 'skip':
        this.advance();
        break;
      case 'cancel':
        this.learning = undefined;
        this.guidedIndex = -1;
        break;
      case 'reset':
        w.input.profile = defaultProfile(w.hid?.device.productId);
        w.saveProfile();
        break;
      case 'done':
        this.close();
        return;
    }
    this.render();
  }

  private startLearn(target: Target, guided: boolean): void {
    const s = this.wheel.source;
    if (!s?.connected) return;
    s.poll?.();
    let learner: Learner | undefined;
    if (target === 'steer') learner = new Learner(s.controls, 'axis');
    else if (target === 'throttle' || target === 'brake' || target === 'clutch') learner = new Learner(s.controls, 'axis', [this.wheel.profile.steer.axis]);
    else if (target !== 'centre' && target !== 'direction') learner = new Learner(s.controls, 'button');
    this.learning = { target, learner, guided, started: performance.now() };
    this.render();
  }

  /** "Next" on the steps that wait for the player rather than for a control. */
  private async next(): Promise<void> {
    const l = this.learning;
    if (!l) return;
    if (l.target === 'direction') {
      const ok = await this.wheel.calibrateDirection();
      this.flash(ok ? 'Force direction saved.' : "The wheel didn't move. Let go of it (and check force feedback is connected), then try again.");
      if (!ok) return;
    }
    this.advance();
  }

  private advance(): void {
    this.learnedAt = performance.now();
    if (this.learning?.guided && this.guidedIndex >= 0 && this.guidedIndex < this.steps.length - 1) {
      this.guidedIndex++;
      this.startLearn(this.steps[this.guidedIndex]!, true);
    } else {
      if (this.learning?.guided) this.flash('Setup complete. Drive a lap and fine-tune the forces here.');
      this.learning = undefined;
      this.guidedIndex = -1;
    }
    this.render();
  }

  private flashText = '';
  private flashUntil = 0;
  private flash(text: string): void {
    this.flashText = text;
    this.flashUntil = performance.now() + 4000;
  }

  private clearBinding(t: Target): void {
    const p = this.wheel.profile;
    if (t === 'clutch') p.clutch = null;
    else if (t === 'throttle' || t === 'brake') delete p[t];
    else delete p.buttons[t as Action];
    this.wheel.saveProfile();
    this.render();
  }

  /** Per-frame: live meters and learn mode. */
  private live(): void {
    const w = this.wheel;
    const s = w.source;
    if (!s?.connected) return;
    s.poll?.();
    const c = s.controls;
    const p = w.profile;
    const steer = steerValue(c, p.steer);
    const fill = this.q('.wheel-steer-fill');
    fill.style.left = `${50 + Math.min(0, steer) * 50}%`;
    fill.style.width = `${Math.abs(steer) * 50}%`;
    this.q('.wheel-steer-label').textContent = `${Math.round((steer * wheelParams.rotation.value) / 2)}°`;
    for (const role of ['throttle', 'brake', 'clutch'] as const) {
      const v = role === 'brake' ? Math.pow(pedalValue(c, p.brake), wheelParams.brakeGamma.value) : pedalValue(c, p[role]);
      (this.el.querySelector(`[data-p="${role}"] div`) as HTMLElement).style.height = `${v * 100}%`;
    }
    const pressed = c.buttons.flatMap((b, i) => (b ? [i] : []));
    const named = pressed.map((i) => {
      const action = (Object.entries(p.buttons) as Array<[Action, number]>).find(([, idx]) => idx === i)?.[0];
      return action ? `${i} (${ACTION_LABELS[action]})` : String(i);
    });
    const axes = c.axes.map((v, i) => `${s.axisNames[i] ?? `Axis ${i}`} ${v.toFixed(2)}`).join(' · ');
    this.q('.wheel-readout').innerHTML = `<div>${axes}</div><div>Buttons: ${named.length ? named.join(', ') : '–'}${c.hat >= 0 ? ` · D-pad ${c.hat}` : ''}</div>`;
    const force = w.ffb.lastTotal;
    const ff = this.q('.wheel-ffb-fill');
    ff.style.left = `${50 + Math.min(0, force) * 50}%`;
    ff.style.width = `${Math.abs(force) * 50}%`;

    this.learnTick();
    if (this.flashUntil && performance.now() > this.flashUntil) {
      this.flashUntil = 0;
      this.render();
    }
  }

  /** Feed the active learner the latest controls (per HID report, and per frame for gamepads). */
  private learnTick(): void {
    const s = this.wheel.source;
    const l = this.learning;
    if (!s?.connected || !l?.learner) return;
    const c = s.controls;
    const p = this.wheel.profile;
    const w = this.wheel;
    const res = l.learner.update(c);
    (this.q('.wheel-prompt-progress div') as HTMLElement).style.width = `${l.learner.progress * 100}%`;
    if (res) {
      if (res.kind === 'axis' && l.target === 'steer') p.steer = { axis: res.axis, rest: res.rest, full: res.full };
      else if (res.kind === 'axis' && (l.target === 'throttle' || l.target === 'brake' || l.target === 'clutch')) p[l.target] = { axis: res.axis, rest: res.rest, full: res.full };
      else if (res.kind === 'button') {
        // One control per action: free the button from anything else it was bound to.
        for (const [a, idx] of Object.entries(p.buttons) as Array<[Action, number]>) if (idx === res.button) delete p.buttons[a];
        p.buttons[l.target as Action] = res.button;
      }
      w.saveProfile();
      this.advance();
    }
  }

  render(): void {
    const w = this.wheel;
    const status = this.q('.wheel-status');
    const parts: string[] = [];
    if (w.status === 'hid') {
      const hid = w.hid!;
      parts.push(`<b>${hid.name}</b> connected over WebHID.`);
      if (hid.ps4Mode) parts.push('Its mode switch is on <b>PS4</b>, which this game can\'t drive: slide the switch on the wheel to <b>PS3</b> (the PC mode), then reconnect.');
      else if (hid.compatMode && /Windows/.test(navigator.userAgent)) parts.push('It is in <b>compatibility mode</b> (no 900°, no separate pedals): open <b>Logitech G HUB</b> so it switches the wheel to native mode, then reconnect.');
      else if (hid.compatMode) parts.push('It is in <b>compatibility mode</b> (no 900°, no separate pedals): press <b>Switch to native mode</b>, then reconnect.');
      else if (hid.native) parts.push('Force feedback <b>on</b>.');
      if (hid.native && !w.profile.forceSignChecked) parts.push('Run <b>Guided setup</b> or <b>Check force direction</b> once.');
      if (hid.lastError) parts.push(`<span class="wheel-error">Last write error: ${hid.lastError}</span>`);
    } else if (w.status === 'gamepad') {
      parts.push(`<b>${w.source!.name}</b> connected as a game controller: steering and pedals work, but browsers give game controllers no force feedback.`);
      if (w.supported) parts.push('For force feedback on a Logitech wheel press <b>Connect wheel</b>.');
    } else if (w.status === 'no-webhid') {
      parts.push('This browser has no WebHID, so no force feedback: use <b>Chrome, Edge or Chromium</b>. A wheel still works for steering and pedals if the browser sees it as a game controller (press a pedal to wake it).');
    } else {
      parts.push('No wheel connected. Press <b>Connect wheel</b> and pick your Logitech wheel.');
    }
    if (w.error) parts.push(`<span class="wheel-error">${w.error}</span>`);
    if (this.flashUntil) parts.push(`<span class="wheel-flash">${this.flashText}</span>`);
    status.innerHTML = parts.join(' ');

    const connected = w.active;
    const show = (act: string, on: boolean): void => {
      (this.el.querySelector(`[data-act="${act}"]`) as HTMLElement).style.display = on ? '' : 'none';
    };
    show('connect', w.supported && !w.hasFfb);
    show('disconnect', connected);
    show('guided', connected && !this.learning);
    show('direction', w.hasFfb && !this.learning);
    show('left', w.hasFfb);
    show('right', w.hasFfb);
    // In compatibility mode the switch command names the wheel; if its USB name doesn't say which, offer both.
    const unknownModel = !!w.hid?.compatMode && !w.hid.compatModel;
    show('native', !!w.hid?.compatMode);
    show('native-g29', unknownModel);
    this.q('[data-act="native"]').textContent = unknownModel ? 'Switch to native mode (G27)' : 'Switch to native mode';
    this.q('.wheel-live').style.display = connected ? '' : 'none';

    const l = this.learning;
    const promptEl = this.q('.wheel-prompt');
    promptEl.style.display = l ? '' : 'none';
    if (l) {
      const step = l.guided ? `Step ${this.guidedIndex + 1} of ${this.steps.length}: ` : '';
      this.q('.wheel-prompt-text').innerHTML = step + prompt(l.target);
      show('next', l.target === 'centre' || l.target === 'direction');
      show('skip', l.guided || l.target === 'direction');
      (this.q('.wheel-prompt-progress') as HTMLElement).style.display = l.learner?.want === 'axis' ? '' : 'none';
    }

    this.renderBindings();
    for (const r of this.valueRenderers) r();
  }

  private renderBindings(): void {
    const p = this.wheel.profile;
    const names = this.wheel.source?.axisNames ?? [];
    const axisText = (b: { axis: number; rest: number; full: number } | undefined | null): string =>
      b ? `${names[b.axis] ?? `Axis ${b.axis}`} ${b.rest.toFixed(2)}→${b.full.toFixed(2)}` : 'Not set';
    const rows: Array<{ target: Target; label: string; value: string; clearable: boolean }> = [
      { target: 'steer', label: 'Steering', value: axisText(p.steer), clearable: false },
      ...(['throttle', 'brake', 'clutch'] as const).map((r) => ({ target: r, label: PEDAL_LABELS[r], value: axisText(p[r]), clearable: r === 'clutch' })),
      ...BUTTON_ACTIONS.map((a) => ({ target: a, label: ACTION_LABELS[a], value: p.buttons[a] !== undefined ? `Button ${p.buttons[a]}` : 'Not set', clearable: p.buttons[a] !== undefined })),
    ];
    const enabled = this.wheel.active ? '' : 'disabled';
    this.q('.wheel-bindings').innerHTML = rows
      .map(
        (r) => `<div class="setting"><div class="setting-label">${r.label}<small>${r.value}</small></div><div class="setting-control">
          ${r.clearable ? `<button class="settings-btn step" data-clear="${r.target}" aria-label="Clear ${r.label}">×</button>` : ''}
          <button class="settings-btn cycle" data-learn="${r.target}" ${enabled}>Learn</button></div></div>`,
      )
      .join('');
  }
}
